import { useState, useEffect } from 'react';
import clsx from 'clsx';

import Typography from 'components/CncUI/Typography';
import Button from 'components/CncUI/Button';
import Radio from 'components/CncUI/Radio';

import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import TextMessageImage from 'images/createAccount/TextMessage.svg';
import VoiceCallImage from 'images/createAccount/VoiceCall.svg';
import PhoneVerificationCard from '../PhoneVerificationCard';
import Loader from 'components/Loader';
import Logger from 'utilities/logger';

import useGetEnrolledFactors from '../hooks/useGetEnrolledFactors';

const useStyles = makeStyles(({ palette, breakpoints }) => ({
  root: {
    display: 'flex',
    width: 484,
    backgroundColor: palette.cncUI.white,
    boxShadow: '0 0 5px 3px rgba(0, 0, 0, 0.1)',
    marginTop: 50,
    marginLeft: 'auto',
    marginRight: 'auto',
    padding: '40px 56px 0 50px',
    alignContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    [breakpoints.down('sm')]: {
      width: '100%',
      marginTop: 0,
      paddingTop: 32,
      paddingLeft: 0,
      paddingRight: 0,
    },
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'center',
    alignItems: 'center',
    '& h1': {
      color: palette.cncUI.default['-9'],
    },
    '& p': {
      color: palette.cncUI.primary.gray['+0'],
    },
    [breakpoints.down('sm')]: {
      width: '100%',
      marginTop: 0,
      padding: '0 14px',
    },
  },
  tips: {
    boxShadow: 'none',
  },
  summary: {
    '& .MuiAccordionSummary-content': {
      flexGrow: 0,
    },
  },
  title: {
    color: palette.cncUI.black,
    marginBottom: 8,
    fontWeight: 400,
    [breakpoints.down('sm')]: {
      fontSize: 24,
    },
  },
  description: {
    width: 390,
    color: '#5c5c5c !important',
    lineHeight: 'normal',
    [breakpoints.down('sm')]: {
      fontSize: 16,
      width: 'auto',
    },
  },
  button: {
    width: 340,
    height: 40,
    textTransform: 'none',
    marginTop: 31,
    marginBottom: 57,
    padding: 0,
    [breakpoints.down('sm')]: {
      width: '100%',
      marginTop: 25,
      marginBottom: 0,
    },
  },
  contained: {
    marginTop: 0,
    height: 40,
    '& span': {
      height: '100%',
      borderRadius: 2,
    },
    [breakpoints.down('sm')]: {
      marginTop: 8,
    },
  },
  buttonContainer: {
    [breakpoints.down('sm')]: {
      width: '100%',
      padding: '0 23px 80px 23px !important',
    },
  },
  leftArrow: {
    borderRadius: '50%',
    backgroundColor: `${palette.cncUI.default.background} !important`,
    color: palette.secondary.main,
    marginRight: 8,
  },
  backButton: {
    marginTop: 0,
    marginBottom: 80,
    textTransform: 'none',
    [breakpoints.down('sm')]: {
      marginTop: 49,
    },
  },
  radio: {
    marginTop: -28,
  },
  radioLabel: {
    width: '100%',
    margin: 0,
  },
  radioItem: {
    marginBottom: 12,
    backgroundColor: 'transparent',
    '& label': {
      margin: 0,
    },
    '& :hover': {
      backgroundColor: '#f4fbfa',
    },
    '& :focus-within': {
      backgroundColor: '#f4fbfa',
    },
    [breakpoints.down('sm')]: {
      maxWidth: '100%',
      margin: '0 auto',
      marginBottom: 8,
    },
  },
  selected: {
    backgroundColor: '#f4fbfa',
  },
  group: {
    [breakpoints.down('sm')]: {
      padding: 0,
      marginTop: 28,
    },
  },
}));

const VerifyIdentity = props => {
  const logger = Logger('accountCreation.verifyIdentity');
  const classes = useStyles();
  const { setStep, changeState, isMobile, state } = props;
  const { tokenData } = state;
  const [loading, setLoading] = useState(true);
  const [phoneVerification, setPhoneVerification] = useState('text');
  const getFactors = useGetEnrolledFactors();

  useEffect(() => {
    getFactors(tokenData.user.id)
      .then(factors => {
        const newState = {};
        const smsFactor = factors.find(f => f.type === 'sms') || {};
        const voiceFactor = factors.find(f => f.type === 'call') || {};
        const oktaVerifyFactor =
          factors.find(
            f => f.type === 'token:software:totp' && f.provider === 'OKTA',
          ) || {};

        if (smsFactor.status === 'ACTIVE') {
          newState.sms = true;
          newState.adding = 'sms';
          newState.phone = (JSON.parse(smsFactor.embedded) || {}).phoneNumber;
        }

        if (voiceFactor.status === 'ACTIVE') {
          newState.call = true;
          newState.adding = 'call';
          newState.phone = (JSON.parse(smsFactor.embedded) || {}).phoneNumber;
        }

        if (oktaVerifyFactor.status === 'ACTIVE') {
          newState.okta = true;
          newState.adding = 'okta';
        }

        if (Object.keys(newState).length > 0) {
          changeState(newState);
          if (newState.sms || newState.call) {
            setStep('AccountCreated');
          }
        }
      })
      .catch(err => {
        logger.error('Factors List', err);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <Loader />;
  }

  const handleClick = () => {
    changeState({ passwordRecovery: phoneVerification });
    setStep(phoneVerification === 'text' ? 'TextMessage' : 'VoiceCall');
  };

  return (
    <Paper id="VerifyIdentityRoot" className={classes.root}>
      <Grid container spacing={1} className={classes.container}>
        <Grid item xs={12} lg={12}>
          <Typography variant="h4" className={classes.title}>
            Phone Number Verification
          </Typography>
          <Typography variant="body2" className={classes.description}>
            Securely add a phone number by selecting one of these methods to
            verify your identity. We'll use this phone number to reset your
            password if you ever forget it and enroll you into MFA (multi-factor
            authentication.)
          </Typography>
        </Grid>
        <Grid item xs={12} lg={12}>
          <Radio.Group
            aria-label="Phone Number Verification"
            name="phone"
            id="VerifyIdentityRadioGroup"
            className={classes.group}
            value={phoneVerification}
            onChange={e => setPhoneVerification(e.target.value)}
          >
            <div
              className={clsx(
                classes.radioItem,
                phoneVerification === 'text' && classes.selected,
              )}
            >
              <Radio id="VerifyIdentityRadioTextMessage" value="text">
                <PhoneVerificationCard
                  image={TextMessageImage}
                  title={`Text Message ${
                    isMobile ? '' : '(cell phone users only)'
                  }`}
                  text="Receive a text message on your mobile device."
                  extra={
                    isMobile
                      ? ''
                      : '(Text messages CANNOT be sent to a landline phone)'
                  }
                />
              </Radio>
            </div>
            <div
              className={clsx(
                classes.radioItem,
                phoneVerification === 'voice' && classes.selected,
              )}
            >
              <Radio id="VerifyIdentityRadioVoice" value="voice">
                <PhoneVerificationCard
                  image={VoiceCallImage}
                  title="Voice"
                  text="Receive a voice call on your mobile device or landline."
                />
              </Radio>
            </div>
          </Radio.Group>
        </Grid>
        <Grid item xs={12} lg={12} className={classes.buttonContainer}>
          <Button
            id="VerifyIdentityAddPhoneButton"
            className={clsx(classes.button, classes.contained)}
            onClick={handleClick}
          >
            Add phone number
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default VerifyIdentity;
